import { isDefined } from "@omni/utils/helper.utils";
import {
    ComponentProps,
    createContext,
    MouseEvent,
    ReactNode,
    RefObject,
    use
} from "react";

type SearchBoxContextValue = {
    isOpen: boolean;
    inputRef: RefObject<HTMLInputElement | null>;
    formRef: RefObject<HTMLFormElement | null>;
    submitButtonRef: RefObject<HTMLButtonElement | null>;
    resetButtonRef: RefObject<HTMLButtonElement | null>;
    inputProps: ComponentProps<"input">;
    formProps: ComponentProps<"form">;
    onCloseClick: (event: MouseEvent) => void;
    onResetClick: (event: MouseEvent) => void;
    onOpenClick: (event: MouseEvent) => void;
};

type SearchBoxContextProviderProps = {
    isOpen: boolean;
    inputRef: RefObject<HTMLInputElement | null>;
    formRef: RefObject<HTMLFormElement | null>;
    submitButtonRef: RefObject<HTMLButtonElement | null>;
    resetButtonRef: RefObject<HTMLButtonElement | null>;
    inputProps: ComponentProps<"input">;
    formProps: ComponentProps<"form">;
    onCloseClick: (event: MouseEvent) => void;
    onResetClick: (event: MouseEvent) => void;
    onOpenClick: (event: MouseEvent) => void;
    children: ReactNode;
};

export const SearchBoxContext = createContext<
    SearchBoxContextValue | undefined
>(undefined);

export const SearchBoxContextProvider = ({
    isOpen,
    inputRef,
    formRef,
    submitButtonRef,
    resetButtonRef,
    inputProps,
    formProps,
    onCloseClick,
    onResetClick,
    onOpenClick,
    children
}: SearchBoxContextProviderProps) => {
    return (
        <SearchBoxContext.Provider
            value={{
                isOpen: isOpen,
                inputRef: inputRef,
                formRef: formRef,
                submitButtonRef: submitButtonRef,
                resetButtonRef: resetButtonRef,
                inputProps: inputProps,
                formProps: formProps,
                onCloseClick: onCloseClick,
                onResetClick: onResetClick,
                onOpenClick: onOpenClick
            }}
        >
            {children}
        </SearchBoxContext.Provider>
    );
};

export const useSearchBoxContext = () => {
    const context = use<SearchBoxContextValue | undefined>(SearchBoxContext);

    if (!isDefined(context)) {
        throw new Error(`${SearchBoxContext.displayName} is undefined.`);
    }

    return context;
};
